import * as React from 'react'
import { VehicleGroupModel } from '@/app/models/vehicle/vehicleGroupModel'
import {
  FaIcon,
  faUsersGroupSolid,
  faUsersMedicalSolid,
  faChevronDown,
  faListCheck,
} from '@/lib/fa'
import AddVehicleGroupModal from '../modals/groups/add/AddVehicleGroupModal'
import EditVehicleGroupModal from '../modals/groups/edit/EditVehicleGroupModal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type DropDownProps = {
  options: VehicleGroupModel[]
  companyId: string
  refreshGroups: () => void
  name?: string
  onSelect?: (option: VehicleGroupModel | null) => void
  actualGroup?: VehicleGroupModel | null
  /** Stretch to the container width, matching the drivers sidebar picker. */
  fullWidth?: boolean
}

const VehicleGroupPicker: React.FC<DropDownProps> = ({
  options,
  companyId,
  refreshGroups,
  name,
  onSelect,
  actualGroup = null,
  fullWidth = false,
}) => {
  const { t } = useLocalize()
  const [open, setOpen] = React.useState(false)
  const [isEditGroupModelOpen, setIsEditGroupModelOpen] = React.useState(false)
  const [isAddGroupModelOpen, setIsAddGroupModelOpen] = React.useState(false)
  const [selected, setSelected] = React.useState<VehicleGroupModel | null>(
    actualGroup,
  )
  const dropdownRef = React.useRef<HTMLUListElement>(null)

  const defaultName = name ?? t('vehicleModule.all-label')

  React.useEffect(() => {
    setSelected(actualGroup)
  }, [actualGroup])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const handleSelect = (option: VehicleGroupModel | null) => {
    setSelected(option)
    setOpen(false)
    onSelect?.(option)
  }

  const allOption: VehicleGroupModel = { id: 0, name: t('vehicleModule.all-label') }
  const activeOption: VehicleGroupModel = { id: -1, name: t('vehicleModule.active-vehicles') }
  const inactiveOption: VehicleGroupModel = { id: -2, name: t('vehicleModule.inactive-vehicles') }

  const resolveDisplayName = (group: VehicleGroupModel | null): string => {
    if (!group) return defaultName
    if (group.id === 0) return allOption.name
    if (group.id === -1) return activeOption.name
    if (group.id === -2) return inactiveOption.name
    return group.name
  }

  React.useEffect(() => {
    if (!actualGroup && !selected) {
      setSelected(activeOption)
      onSelect?.(activeOption)
    }
  }, [])

  const fullOptions = [
    allOption,
    activeOption,
    inactiveOption,
    ...options.filter((opt) => opt.id !== 0 && opt.id > 0),
  ]

  const filteredOptions = fullOptions.filter(
    (opt) => opt.id !== (selected?.id ?? 0),
  )

  return (
    <div
      className={`tw-relative tw-z-[100] ${fullWidth ? 'tw-w-full' : 'tw-w-fit tw-flex-shrink-0'}`}
    >
      <button
        onClick={(e) => {
          e.stopPropagation()
          setOpen((prev) => !prev)
        }}
        className={`tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-bg-transparent tw-px-2 tw-py-1 tw-text-base-content hover:tw-bg-[#F5F5F5] ${fullWidth ? 'tw-w-full' : ''} ${
          open
            ? 'tw-border-2 tw-border-primary'
            : 'tw-border tw-border-neutral/20'
        }`}
      >
        <p className="tw-text-sm">{resolveDisplayName(selected)}</p>
        <FaIcon
          icon={faChevronDown}
          className={`tw-ml-2 tw-h-4 tw-w-4 tw-transition-transform tw-duration-200 ${
            open ? 'tw-rotate-180' : ''
          }`}
        />
      </button>

      <ul
        ref={dropdownRef}
        className={`tw-absolute tw-left-0 tw-z-50 tw-mt-2 tw-w-fit ${fullWidth ? 'tw-w-full' : 'tw-max-w-[400px]'} tw-overflow-hidden tw-rounded tw-bg-white tw-p-1.5 tw-shadow-md tw-transition-all tw-duration-200 ${
          open
            ? 'tw-scrollbar-thin tw-max-h-[500px] tw-overflow-y-auto tw-opacity-100'
            : 'tw-max-h-0 tw-opacity-0'
        }`}
      >
        {selected ? (
          <li
            key={selected.id}
            className="tw-font-sm tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-text-ellipsis tw-rounded tw-bg-[#E9EDF9] tw-px-1 tw-py-2"
            onClick={() => handleSelect(selected)}
          >
            <p>{resolveDisplayName(selected)}</p>
            <FaIcon icon={faListCheck} className="tw-text-primary" />
          </li>
        ) : (
          <li
            key={allOption.id}
            className="tw-font-sm tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-text-ellipsis tw-rounded tw-bg-[#E9EDF9] tw-px-1 tw-py-2"
            onClick={() => handleSelect(allOption)}
          >
            <p>{allOption.name}</p>
            <FaIcon icon={faListCheck} className="tw-text-primary" />
          </li>
        )}

        <li className="tw-mb-1.5 tw-mt-2.5 tw-border-t tw-border-gray-200"></li>

        {filteredOptions.map((option) => (
          <li
            key={option.id}
            className="tw-font-sm tw-cursor-pointer tw-text-ellipsis tw-rounded tw-px-1 tw-py-2 hover:tw-bg-gray-100"
            onClick={() => handleSelect(option.id === 0 ? null : option)}
          >
            {option.name}
          </li>
        ))}

        <div className="tw-sticky tw-bottom-0 tw-bg-inherit">
          <li className="tw-my-1 tw-border-t tw-border-gray-200"></li>
          <li
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded tw-px-1 tw-py-2 tw-text-gray-500 hover:tw-bg-gray-100"
            onClick={() => {
              setIsAddGroupModelOpen(true)
              setOpen(false)
            }}
          >
            <FaIcon icon={faUsersMedicalSolid} className="tw-text-primary" />
            <p className="tw-text-nowrap tw-text-sm tw-font-medium tw-text-primary">
              {t('vehicleModule.add-new-group')}...
            </p>
          </li>
          <li
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded tw-px-1 tw-py-2 tw-text-gray-500 hover:tw-bg-gray-100"
            onClick={() => {
              setIsEditGroupModelOpen(true)
              setOpen(false)
            }}
          >
            <FaIcon icon={faUsersGroupSolid} className="tw-text-primary" />
            <p className="tw-text-nowrap tw-text-sm tw-font-medium tw-text-primary">
              {t('vehicleModule.edit-groups')}
            </p>
          </li>
        </div>
      </ul>
      {isEditGroupModelOpen && options.length > 0 && (
        <EditVehicleGroupModal
          companyId={companyId}
          groups={options}
          close={() => setIsEditGroupModelOpen(false)}
        />
      )}
      {isAddGroupModelOpen && (
        <AddVehicleGroupModal
          openGroupMembers={true}
          companyId={companyId}
          refreshGroups={refreshGroups}
          close={() => setIsAddGroupModelOpen(false)}
        />
      )}
    </div>
  )
}

export default VehicleGroupPicker
